import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { formatMoney, priceWithTax } from "../../../utils/pricing";

const capitalize = (value = "") => {
  const text = String(value ?? "");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getImageUrl = (image) => {
  if (typeof image === "string") return image;
  return image?.url ?? image?.src ?? image?.path ?? null;
};

const handleKeyboardSelection = (event, callback) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    callback();
  }
};

const buildCombinations = (productData) => {
  const variants = productData?.variants?.filter((variant) => variant.isActive) || [];
  if (variants.length === 0) return null;

  const optionGroupsMap = new Map();

  variants.forEach((variant) => {
    (variant.options || []).forEach((option) => {
      if (option.optionGroup) {
        optionGroupsMap.set(option.optionGroup.optionGroupId, {
          optionGroupId: option.optionGroup.optionGroupId,
          name: option.optionGroup.name,
        });
      }
    });
  });

  const optionGroups = Array.from(optionGroupsMap.values());
  if (optionGroups.length === 0) return null;

  const preferredNames = ["Tamaño del pan", "Tamaño", "Size"];
  const primaryGroup =
    optionGroups.find((group) =>
      preferredNames.some((name) => String(group.name || "").toLowerCase() === name.toLowerCase()),
    ) || optionGroups[0];

  const groupedByPrimary = {};

  variants.forEach((variant) => {
    const options = variant.options || [];
    const primaryOption = options.find(
      (option) => option.optionGroup?.optionGroupId === primaryGroup.optionGroupId,
    );

    if (!primaryOption) return;

    const key = primaryOption.optionId;
    if (!groupedByPrimary[key]) {
      groupedByPrimary[key] = {
        primaryOption,
        variants: [],
      };
    }

    groupedByPrimary[key].variants.push({
      ...variant,
      secondaryOptions: options.filter(
        (option) => option.optionGroup?.optionGroupId !== primaryGroup.optionGroupId,
      ),
    });
  });

  const groupedArray = Object.values(groupedByPrimary);
  if (groupedArray.length === 0) return null;

  return { groupedArray, primaryGroup };
};

export const getDefaultVariantId = (productData) => {
  const combinations = buildCombinations(productData);
  return combinations?.groupedArray[0]?.variants[0]?.variantId ?? "";
};

export const Combinations = ({ productData, variantId, setVariantId }) => {
  const [selectedPrimaryOptionId, setSelectedPrimaryOptionId] = useState(null);
  const corporationLogo = useSelector((state) => state.aplicationConfig.corporationData.info.logo);
  const combinations = buildCombinations(productData);
  const selectedPrimaryGroup = combinations?.groupedArray.find(
    (group) => String(group.primaryOption.optionId) === String(selectedPrimaryOptionId),
  );
  const groupForSelectedVariant = combinations?.groupedArray.find((group) =>
    group.variants.some((variant) => String(variant.variantId) === String(variantId)),
  );
  const selectedGroup =
    selectedPrimaryGroup || groupForSelectedVariant || combinations?.groupedArray[0] || null;
  const defaultVariantId = selectedGroup?.variants[0]?.variantId ?? getDefaultVariantId(productData);
  const selectedVariantBelongsToActiveGroup = selectedGroup?.variants.some(
    (variant) => String(variant.variantId) === String(variantId),
  );

  useEffect(() => {
    if ((!variantId || !selectedVariantBelongsToActiveGroup) && defaultVariantId) {
      setVariantId(defaultVariantId);
    }
  }, [defaultVariantId, selectedVariantBelongsToActiveGroup, setVariantId, variantId]);

  if (!combinations) return null;

  const { groupedArray, primaryGroup } = combinations;
  const activePrimaryOptionId = selectedGroup.primaryOption.optionId;
  const variantsToShow = selectedGroup.variants || [];

  const selectPrimaryOption = (primaryOptionId) => {
    const nextGroup = groupedArray.find(
      (group) => String(group.primaryOption.optionId) === String(primaryOptionId),
    );
    if (!nextGroup || String(primaryOptionId) === String(activePrimaryOptionId)) return;

    setSelectedPrimaryOptionId(primaryOptionId);
    setVariantId(nextGroup.variants[0]?.variantId || "");
  };

  return (
    <>
      <h5>
        <b>Combinaciones</b>
      </h5>

      <div className="combination-group-label">{capitalize(primaryGroup.name)}</div>
      <div className="combination-primary-list" role="radiogroup" aria-label={primaryGroup.name}>
        {groupedArray.map(({ primaryOption }) => {
          const isSelected = String(primaryOption.optionId) === String(activePrimaryOptionId);

          return (
            <div
              key={primaryOption.optionId}
              className={`combination-primary-option ${isSelected ? "is-selected" : ""}`}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => selectPrimaryOption(primaryOption.optionId)}
              onKeyDown={(event) =>
                handleKeyboardSelection(event, () => selectPrimaryOption(primaryOption.optionId))
              }
            >
              {capitalize(primaryOption.name)}
            </div>
          );
        })}
      </div>

      <div className="combination-secondary-list" role="radiogroup" aria-label="Variante">
        {variantsToShow.map((variant, index) => {
          const isSelected = String(variant.variantId) === String(variantId);
          const secondaryName = variant.secondaryOptions
            .map((option) => capitalize(option.name))
            .filter(Boolean)
            .join(" · ");
          const label = secondaryName || capitalize(selectedGroup.primaryOption.name);
          const imageUrl = getImageUrl(variant.images?.[0]) || corporationLogo;

          return (
            <div
              key={`${variant.variantId}-grouped-${index}`}
              className={`combination-secondary-option ${isSelected ? "is-selected" : ""}`}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onClick={() => setVariantId(variant.variantId)}
              onKeyDown={(event) =>
                handleKeyboardSelection(event, () => setVariantId(variant.variantId))
              }
            >
              <div className="combination-variant-image">
                {imageUrl ? <img src={imageUrl} alt="" /> : null}
              </div>
              <span>{label}</span>
              <small>+{formatMoney(priceWithTax(Number(variant.price) || 0, productData))}</small>
            </div>
          );
        })}
      </div>
    </>
  );
};
